// Parse text into blocks (paragraphs, lists, spacers, headings)
export interface TextBlock {
  type: 'paragraph' | 'list' | 'spacer' | 'heading'
  value?: string
  items?: string[]
  level?: number // for headings: 1-6
}

export function parseBlocks(content: string): TextBlock[] {
  const lines = content.split('\n')
  const blocks: TextBlock[] = []
  let pendingList: string[] = []

  function flushList() {
    if (pendingList.length > 0) {
      blocks.push({ type: 'list', items: pendingList })
      pendingList = []
    }
  }

  for (const raw of lines) {
    const line = raw.trimEnd()

    // List item
    if (line.trim().startsWith('- ')) {
      pendingList.push(line.trim().slice(2))
      continue
    }

    flushList()

    // Empty line (spacer)
    if (line.trim() === '') {
      if (blocks.at(-1)?.type !== 'spacer') {
        blocks.push({ type: 'spacer' })
      }
      continue
    }

    // Heading (## text or ### text, etc.)
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/)
    if (headingMatch) {
      const level = headingMatch[1].length
      const text = headingMatch[2]
      blocks.push({ type: 'heading', value: text, level })
      continue
    }

    // Regular paragraph
    blocks.push({ type: 'paragraph', value: line })
  }

  flushList()

  return blocks
}

export function repairAssistantStructure(content: string): string {
  if (!content) return content

  let output = content

  // Normalize malformed italic/bold markers with extra inner whitespace (e.g., "* either.*").
  output = output.replace(/\*\s+([^*\n][^*\n]*?)\s*\*/g, (_, inner: string) => `*${inner.trim()}*`)
  output = output.replace(/\*\*\s+([^*\n][^*\n]*?)\s*\*\*/g, (_, inner: string) => `**${inner.trim()}**`)

  // Repair glued sentence boundaries like "text.Next sentence".
  output = output.replace(/([.!?]["')\]]?)([A-Z])/g, '$1 $2')

  // Insert paragraph break before bold headings glued to preceding text (e.g., "300**Mexico Drug Cartel Violence**").
  // Matches any non-whitespace char immediately before **CapitalizedText** (3+ chars).
  output = output.replace(/([^\s\n])(\*\*[A-Z][^*\n]{2,}\*\*)/g, '$1\n\n$2')

  // Repair glued markdown emphasis boundaries like "**Title**Paragraph" or "**Title**–Description".
  output = output.replace(/(\*\*[^*\n]{1,200}\*\*)(?=[A-Za-z0-9–—])/g, '$1\n\n')
  output = output.replace(/(\*[^*\n]{1,200}\*)(?=[A-Za-z0-9])/g, '$1 ')

  // Insert paragraph break after quoted dialogue entries (e.g., `"..."The next line`).
  output = output.replace(/((?:["“][^"\n“”]{20,}["”]))\s+([A-Z])/g, '$1\n\n$2')

  // Insert paragraph break after timeline/year quote lines (e.g., `2017: "..."The final`).
  output = output.replace(/(\b\d{4}:\s*"[^"\n]{8,}")\s+([A-Z])/g, '$1\n\n$2')

  // Ensure markdown headings are on their own line.
  output = output.replace(/([^\n])\s*(#{2,6}\s+)/g, '$1\n\n$2')

  // Ensure bullet items aren't glued to the previous sentence.
  output = output.replace(/([.!?:])\s*(-\s+[A-Za-z(])/g, '$1\n$2')

  // Ensure ordered list markers have a space (e.g., "1.Item" -> "1. Item").
  output = output.replace(/(^|[\s(])(\d{1,3})\.(?=[^\s\d])/gm, '$1$2. ')
  output = output.replace(/(\d{1,2}\.)(\*\*)/g, '$1 $2')

  // Ensure section labels followed by ordered items start on a new line.
  output = output.replace(/(:)\s*(\d{1,3}\.\s*(?:\*\*\s*)?[A-Za-z_])/g, '$1\n$2')

  // Ensure inline ordered list items are split onto separate lines.
  output = output.replace(/([^\n])\s+(?=(\d{1,3}\.\s*(?:\*\*\s*)?[A-Za-z_]))/g, '$1\n')
  output = output.replace(/([^\n\d])(?=(\d{1,3}\.\s*(?:\*\*\s*)?[^\s\d]))/g, '$1\n')
  output = output.replace(/(\b\d{4})(?=(\d{1,3}\.\s*(?:\*\*\s*)?[A-Za-z_]))/g, '$1\n')

  // Re-normalize bold markers after list spacing fixes (e.g., "3. ** Name**").
  output = output.replace(/\*\*\s+([^*\n][^*\n]*?)\s*\*\*/g, (_, inner: string) => `**${inner.trim()}**`)

  // Ensure markdown horizontal rules are isolated on their own line.
  output = output.replace(/([.!?])\s*(---+)(?=\s|$)/g, '$1\n\n$2')

  // Add at least spacing after a colon when the next token is glued.
  output = output.replace(/:([A-Z])/g, ': $1')

  // Promote short "section label:" phrases into paragraph starts.
  output = output.replace(
    /([.!?:])\s+([A-Z][A-Za-z0-9'’]*(?: [A-Za-z0-9'’]+){0,7}:)(?=\s|$)/g,
    '$1\n\n$2',
  )
  output = output.replace(/([^\n])\s*(Active Assets:|Archived Assets:|Inactive Assets:)(?=\s|$)/g, '$1\n\n$2')

  // Bold section labels should also start their own paragraph.
  output = output.replace(
    /([^\n])\s*(\*\*[A-Z][^*\n]{1,80}:\*\*)(?=\s|$)/g,
    '$1\n\n$2',
  )

  // Add a space before parenthesized file metadata (e.g., "file.jpg(123 KB)").
  output = output.replace(/(\*\*[^*\n]{1,200}\*\*)\(/g, '$1 (')
  output = output.replace(/([A-Za-z0-9_.-]+\.[A-Za-z0-9]{2,8})\(/g, '$1 (')

  return output.replace(/\n{3,}/g, '\n\n').trim()
}

export function normalizeAssistantContent(content: string): string {
  if (!content) return content

  let output = content.replace(/\r\n/g, '\n')

  if (/<\s*\/?\s*[a-z]/i.test(output)) {
    output = output.replace(/<\s*br\s*\/?\s*>/gi, '\n')
    output = output.replace(/<\/\s*(p|div|section|article|header|footer|blockquote|pre|ul|ol)\s*>/gi, '\n')
    output = output.replace(/<\s*li[^>]*>/gi, '\n- ')
    output = output.replace(/<\/\s*li\s*>/gi, '')
    output = output.replace(
      /<\s*a[^>]*href\s*=\s*['"]([^'"]+)['"][^>]*>(.*?)<\/\s*a\s*>/gi,
      (_, href, label) => {
        const text = String(label).replace(/<[^>]+>/g, '').trim()
        return text ? `[${text}](${href})` : href
      },
    )
    output = output.replace(/<[^>]+>/g, '')
  }

  return repairAssistantStructure(output)
}
